/**
 * RecipeDiscoveryToast -- listens for MSG_RECIPE_DISCOVERED on the server room
 * and also MSG_HOME_COOK_COMPLETE on the HomeRoom, tracking which recipes the
 * player has discovered (session-scoped via game.registry). On first discovery
 * of a recipe, shows a warm toast.
 *
 * Dual-source: the server sends MSG_RECIPE_DISCOVERED on first-ever cook
 * (persisted in recipe_discoveries table), but the client also tracks
 * session-local discovery via MSG_HOME_COOK_COMPLETE for immediate feedback
 * when the toast is wired to a room that receives cook completion events.
 *
 * Follows the WhisperToast/FestivalToast pattern: scene-owned, destroy()-able,
 * no global state beyond game.registry.
 *
 * SMA-76 — Recipe Discovery Book.
 */

import * as Phaser from 'phaser';
import {
  MSG_HOME_COOK_COMPLETE,
  MSG_RECIPE_DISCOVERED,
  getCookingRecipe,
  type HomeCookCompleteEvent,
  type RecipeDiscoveredEvent,
} from '@game/shared';

const TOAST_HOLD_MS = 3500;
const TOAST_FADE_MS = 800;
const TOAST_BOTTOM_OFFSET = 90;
const TOAST_FONT = "'Almendra', serif";
const TOAST_FONT_SIZE = '16px';
const TOAST_COLOR = '#eae0c8';

const REGISTRY_KEY = 'recipes.discoveredIds';

export class RecipeDiscoveryToast {
  private scene: Phaser.Scene;
  private room: { onMessage: (type: string, cb: (payload: unknown) => void) => void } | null;
  private toastText: Phaser.GameObjects.Text | null = null;
  private activeTween: Phaser.Tweens.Tween | null = null;

  constructor(
    scene: Phaser.Scene,
    room: { onMessage: (type: string, cb: (payload: unknown) => void) => void } | null,
  ) {
    this.scene = scene;
    this.room = room;
    this.wireListener();
  }

  private wireListener(): void {
    if (!this.room) return;

    // Client-side tracking via cook completion (HomeRoom path).
    this.room.onMessage(MSG_HOME_COOK_COMPLETE, (raw: unknown) => {
      const payload = raw as HomeCookCompleteEvent;
      const recipeId = payload.recipeId;
      if (!recipeId) return;

      // Get or create the discovery set in game.registry
      let discovered = this.scene.game.registry.get(REGISTRY_KEY) as Set<string> | undefined;
      if (!discovered) {
        discovered = new Set<string>();
        this.scene.game.registry.set(REGISTRY_KEY, discovered);
      }

      // Check if this is a new discovery
      if (discovered.has(recipeId)) return;
      discovered.add(recipeId);

      // Look up the recipe name
      const recipe = getCookingRecipe(recipeId);
      const name = recipe?.name ?? 'Unknown Dish';

      this.showToast(`New Recipe Discovered! ${name}`);
    });

    // Server-authoritative discovery (OverworldRoom/HomeRoom path via
    // MSG_RECIPE_DISCOVERED). Also updates the registry so the journal
    // tab sees it immediately.
    this.room.onMessage(MSG_RECIPE_DISCOVERED, (raw: unknown) => {
      const payload = raw as RecipeDiscoveredEvent;
      if (!payload?.recipeName) return;

      let discovered = this.scene.game.registry.get(REGISTRY_KEY) as Set<string> | undefined;
      if (!discovered) {
        discovered = new Set<string>();
        this.scene.game.registry.set(REGISTRY_KEY, discovered);
      }

      // If the client-side path already fired for this recipe, skip
      // the toast (avoid duplicate).
      if (discovered.has(payload.recipeId)) return;
      discovered.add(payload.recipeId);

      this.showToast(`New Recipe Discovered! ${payload.recipeName}`);
    });
  }

  private showToast(message: string): void {
    if (this.activeTween) {
      this.activeTween.destroy();
      this.activeTween = null;
    }
    if (this.toastText) {
      this.toastText.destroy();
      this.toastText = null;
    }

    const cam = this.scene.cameras.main;
    const x = cam.width / 2;
    const y = cam.height - TOAST_BOTTOM_OFFSET;

    this.toastText = this.scene.add.text(x, y, message, {
      fontSize: TOAST_FONT_SIZE,
      color: TOAST_COLOR,
      fontFamily: TOAST_FONT,
      align: 'center',
      shadow: {
        offsetX: 1,
        offsetY: 1,
        color: '#000000',
        blur: 4,
        fill: true,
        stroke: true,
      },
    });
    this.toastText.setOrigin(0.5, 0.5);
    this.toastText.setAlpha(0);
    this.toastText.setDepth(200);
    this.toastText.setScrollFactor(0);

    this.activeTween = this.scene.tweens.add({
      targets: this.toastText,
      alpha: { from: 0, to: 1 },
      duration: TOAST_FADE_MS,
      ease: 'Sine.easeIn',
      hold: TOAST_HOLD_MS,
      yoyo: true,
      onComplete: () => {
        this.toastText?.destroy();
        this.toastText = null;
        this.activeTween = null;
      },
    });
  }

  destroy(): void {
    if (this.activeTween) {
      this.activeTween.destroy();
      this.activeTween = null;
    }
    if (this.toastText) {
      this.toastText.destroy();
      this.toastText = null;
    }
    this.room = null;
  }
}
